import { useState } from "react";
import { useRevalidator } from "infrastructure/router";
import { useSimpleRequest, type RequestSettings } from "hooks";
import { BooleanSwitchValue } from "consts/general";
import { delay, when } from "util/support";
import useUser from "features/auth/useUser";
import type { WithAction } from "./types";

export const useOnboardingAction = <T extends object>({
    save,
    ...requestSettings
}: WithAction<T>) => {
    const [isLoading, setLoading] = useState(false);

    const { revalidate } = useRevalidator();

    const request = useSimpleRequest({
        ...requestSettings,
        setLoading
    });

    const { user } = useUser();

    const submit = (params: T, requestSettings: RequestSettings = {}) => (
        request(() => save({
            ...params,
            userId: user.coreId,
            isIncomplete: BooleanSwitchValue.Off,
            requestedChangeFields: null
        }), requestSettings)
    );

    const complete = async (params: T, { onSuccess, ...requestSettings }: RequestSettings = {}) => {
        const response = await submit(params, requestSettings);

        await when(
            response.success,
            () => delay(300)
                .then(() => revalidate())
                .then(() => onSuccess?.(response))
        );

        return response;
    };

    return {
        isLoading,
        submit,
        complete
    };
};

export default useOnboardingAction;
